import { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  alpha,
} from '@mui/material';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme } from '../theme/colors';
import { StockStatus } from '../hooks/useProductSearch';

export interface ProductFiltersProps {
  categories: string[];
  selectedCategory?: string;
  onCategoryChange: (category: string) => void;
  minPrice?: number;
  maxPrice?: number;
  onPriceChange: (min: number | undefined, max: number | undefined) => void;
  stockStatus?: StockStatus;
  onStockStatusChange: (status: StockStatus) => void;
}

interface FilterButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const FilterButton = ({ label, isActive, onClick }: FilterButtonProps) => {
  const { mode } = useContext(ColorModeContext);

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
  };

  return (
    <Button
      onClick={onClick}
      fullWidth
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 2,
        py: 1.5,
        borderRadius: theme.radius.sm,
        border: `2px solid ${isActive ? palette.primary : colors.border}`,
        backgroundColor: isActive ? alpha(palette.primary, 0.1) : colors.surface,
        color: isActive ? palette.primary : colors.textPrimary,
        textTransform: 'uppercase',
        fontFamily: typography.fontFamily.mono,
        fontWeight: isActive ? typography.fontWeight.bold : typography.fontWeight.medium,
        fontSize: typography.fontSize.xs,
        letterSpacing: typography.letterSpacing.wide,
        transition: theme.transitions.fast,
        '&:hover': {
          backgroundColor: isActive ? alpha(palette.primary, 0.15) : colors.surfaceElevated,
          borderColor: palette.primary,
        },
      }}
    >
      {label}
    </Button>
  );
};

export const ProductFilters = ({
  categories,
  selectedCategory,
  onCategoryChange,
  minPrice,
  maxPrice,
  onPriceChange,
  stockStatus = StockStatus.ALL,
  onStockStatusChange,
}: ProductFiltersProps) => {
  const { mode } = useContext(ColorModeContext);
  const [localMinPrice, setLocalMinPrice] = useState<string>(
    minPrice?.toString() || ''
  );
  const [localMaxPrice, setLocalMaxPrice] = useState<string>(
    maxPrice?.toString() || ''
  );

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const handleMinPriceChange = (value: string) => {
    setLocalMinPrice(value);
    const numValue = value === '' ? undefined : parseFloat(value);
    if (value === '' || !isNaN(numValue!)) {
      onPriceChange(numValue, maxPrice);
    }
  };

  const handleMaxPriceChange = (value: string) => {
    setLocalMaxPrice(value);
    const numValue = value === '' ? undefined : parseFloat(value);
    if (value === '' || !isNaN(numValue!)) {
      onPriceChange(minPrice, numValue);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        borderRadius: theme.radius.md,
        border: `2px solid ${colors.border}`,
        p: 3,
      }}
    >
      {/* Categories Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mb: 2,
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.widest,
          }}
        >
          Category
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FilterButton
            label="All Categories"
            isActive={!selectedCategory || selectedCategory === ''}
            onClick={() => onCategoryChange('')}
          />
          {categories.map((category) => (
            <FilterButton
              key={category}
              label={category}
              isActive={selectedCategory === category}
              onClick={() => onCategoryChange(category)}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3, borderColor: colors.border }} />

      {/* Price Range Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mb: 2,
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.widest,
          }}
        >
          Price Range (DZD)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Min"
            value={localMinPrice}
            onChange={(e) => handleMinPriceChange(e.target.value)}
            type="number"
            InputProps={{
              inputProps: { min: 0, step: 10 },
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: theme.radius.sm,
                backgroundColor: colors.surfaceElevated,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': {
                  borderColor: colors.border,
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
            }}
          >
            to
          </Typography>
          <TextField
            size="small"
            placeholder="Max"
            value={localMaxPrice}
            onChange={(e) => handleMaxPriceChange(e.target.value)}
            type="number"
            InputProps={{
              inputProps: { min: 0, step: 10 },
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: theme.radius.sm,
                backgroundColor: colors.surfaceElevated,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': {
                  borderColor: colors.border,
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
              },
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3, borderColor: colors.border }} />

      {/* Stock Status Filter */}
      <Box>
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mb: 2,
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.widest,
          }}
        >
          Stock Status
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FilterButton
            label="All Stock"
            isActive={stockStatus === StockStatus.ALL}
            onClick={() => onStockStatusChange(StockStatus.ALL)}
          />
          <FilterButton
            label="In Stock"
            isActive={stockStatus === StockStatus.IN_STOCK}
            onClick={() => onStockStatusChange(StockStatus.IN_STOCK)}
          />
          <FilterButton
            label="Low Stock"
            isActive={stockStatus === StockStatus.LOW_STOCK}
            onClick={() => onStockStatusChange(StockStatus.LOW_STOCK)}
          />
          <FilterButton
            label="Out of Stock"
            isActive={stockStatus === StockStatus.OUT_OF_STOCK}
            onClick={() => onStockStatusChange(StockStatus.OUT_OF_STOCK)}
          />
        </Box>
      </Box>
    </Box>
  );
};
